import { Injectable } from "@angular/core";
import { TermLifeInsuranceStaticDocumentType } from "src/app/quote/term-life-insurance/services/backend/shared/term-life-insurance-document-type";
import { DateTimeService } from "src/app/shared/services/date-time.service";
import {
  Beneficiary,
  BeneficiarySubType,
  QuoteFeatureState,
  StaticDocumentType,
  UnderwritingQuestionAnswer,
} from "src/app/shared/store/quote/term-life-insurance/quote.state";
import { DEFAULT_RESET_VALUE } from "src/app/shared/store/shared/http-request-state";
import { UnreachableCaseError } from "ts-essentials";
import {
  AnsweredUnderwritingQuestionDto,
  OrganizationBeneficiaryDto,
  PersonBeneficiaryDto,
} from "../dto/shared.dto";
import { TermLifeInsuranceConstantRequestDto } from "../request/term-life-insurance-request.dto";

@Injectable({
  providedIn: "root",
})
export class QuoteMapperService {
  createCreateQuoteRequest(quoteFeatureState: QuoteFeatureState) {
    const { personBeneficiariesDto, organizationBeneficiariesDto } =
      this.mapPersonBeneficiaryToDto(quoteFeatureState.beneficiaries);

    const requestDto: TermLifeInsuranceConstantRequestDto = {
      dateOfBirth: DateTimeService.tryToIsoDate(quoteFeatureState.dateOfBirth),
      gender: quoteFeatureState.gender,
      isSmoker: quoteFeatureState.isSmoker,
      email: quoteFeatureState.email,
      hasTermsAccepted: quoteFeatureState.hasTermsAccepted,
      firstName: quoteFeatureState.firstName,
      lastName: quoteFeatureState.lastName,
      mobilePhoneNumber: quoteFeatureState.mobilePhoneNumber,
      addressLines: quoteFeatureState.addressLines,
      city: quoteFeatureState.city,
      country: quoteFeatureState.country,
      postcode: quoteFeatureState.postcode,
      nationalityCode: quoteFeatureState.nationalityCode,
      occupationId: quoteFeatureState.occupationId,
      weightInKg: quoteFeatureState.weightInKg,
      heightInCm: quoteFeatureState.heightInCm,
      hasUsCitizenship: quoteFeatureState.hasUsCitizenship,
      hasSwissTaxResidence: quoteFeatureState.hasSwissTaxResidence,
      monthOfPregnancy: quoteFeatureState.monthOfPregnancy,
      civilStatus: quoteFeatureState.civilStatus,
      swissResidencePermit: quoteFeatureState.swissResidencePermit,
      hasUnderwritingQuestionsPassed: this.getHasUnderwritingQuestionsPassed(
        quoteFeatureState.hasUnderwritingQuestionsPassed,
      ),
      attributionCode: quoteFeatureState.attributionCode,
      answeredUnderwritingQuestions:
        this.mapAnsweredUnderwritingQuestionsToRequestDto(
          quoteFeatureState.answeredUnderwritingQuestions,
        ),
      useStandardBeneficiaries: quoteFeatureState.useStandardBeneficiaries,
      personBeneficiaries: personBeneficiariesDto,
      organizationBeneficiaries: organizationBeneficiariesDto,
      insuranceStartDate: this.getInsuranceStartDate(
        quoteFeatureState.insuranceStartDate,
      ),
      insuredSum: quoteFeatureState.insuredSum,
      insuranceTermInYears: quoteFeatureState.insuranceTermInYears,
      dataAnalyticsCampaign: quoteFeatureState.dataAnalyticsCampaign,
    };

    return requestDto;
  }

  getTermLifeInsuranceStaticDocumentType(
    staticDocumentType: StaticDocumentType,
  ) {
    switch (staticDocumentType) {
      case StaticDocumentType.INFO_DATA_PROCESSING:
        return TermLifeInsuranceStaticDocumentType.INFO_DATA_PROCESSING;
      case StaticDocumentType.AGREEMENT_EMAIL_COMMUNICATION:
        return TermLifeInsuranceStaticDocumentType.AGREEMENT_EMAIL_COMMUNICATION;
      case StaticDocumentType.TERMS_GENERAL_INDIVIDUAL_LIFE_INSURANCE:
        return TermLifeInsuranceStaticDocumentType.TERMS_GENERAL_INDIVIDUAL_LIFE_INSURANCE;
      case StaticDocumentType.TERMS_SUPPLEMENT_TERM_LIFE_INSURANCE:
        return TermLifeInsuranceStaticDocumentType.TERMS_SUPPLEMENT_TERM_LIFE_INSURANCE;
      case StaticDocumentType.TERMS_NON_SMOKER:
        return TermLifeInsuranceStaticDocumentType.TERMS_NON_SMOKER;
      case StaticDocumentType.INFO_ELECTRONIC_DIRECT_SALES:
        return TermLifeInsuranceStaticDocumentType.INFO_ELECTRONIC_DIRECT_SALES;
      case StaticDocumentType.INFO_TERM_LIFE_INSURANCE_PRODUCT:
        return TermLifeInsuranceStaticDocumentType.INFO_TERM_LIFE_INSURANCE_PRODUCT;
      default:
        throw new UnreachableCaseError(staticDocumentType);
    }
  }

  private getHasUnderwritingQuestionsPassed(
    hasUnderwritingQuestionsPassed: boolean | undefined | null,
  ) {
    return hasUnderwritingQuestionsPassed === DEFAULT_RESET_VALUE
      ? undefined
      : hasUnderwritingQuestionsPassed;
  }

  private getInsuranceStartDate(insuranceStartDate: Date | undefined | null) {
    return insuranceStartDate === DEFAULT_RESET_VALUE
      ? undefined
      : DateTimeService.tryToIsoDate(insuranceStartDate);
  }

  private mapAnsweredUnderwritingQuestionsToRequestDto(
    answeredUnderwritingQuestions?: UnderwritingQuestionAnswer[] | null,
  ) {
    return answeredUnderwritingQuestions?.map(
      (answeredUnderwritingQuestion) => {
        const answeredUnderwritingQuestionDto: AnsweredUnderwritingQuestionDto =
          {
            questionId: answeredUnderwritingQuestion.questionId,
          };

        return answeredUnderwritingQuestionDto;
      },
    );
  }

  private mapPersonBeneficiaryToDto(beneficiaries?: Beneficiary[] | null) {
    const personBeneficiariesDto: PersonBeneficiaryDto[] = [];
    const organizationBeneficiariesDto: OrganizationBeneficiaryDto[] = [];

    beneficiaries?.forEach((beneficiary: Beneficiary) => {
      if (beneficiary.type === BeneficiarySubType.PERSON) {
        const personBeneficiaryDto: PersonBeneficiaryDto = {
          index: beneficiary.index,
          percentage: beneficiary.percentage,
          firstName: beneficiary.firstName,
          lastName: beneficiary.lastName,
          dateOfBirth: beneficiary.dateOfBirth.toISOString(),
        };

        personBeneficiariesDto.push(personBeneficiaryDto);
      }

      if (beneficiary.type === BeneficiarySubType.ORGANIZATION) {
        const organizationBeneficiaryDto: OrganizationBeneficiaryDto = {
          index: beneficiary.index,
          percentage: beneficiary.percentage,
          name: beneficiary.name,
          postcode: beneficiary.postcode,
          city: beneficiary.city,
        };

        organizationBeneficiariesDto.push(organizationBeneficiaryDto);
      }
    });

    return {
      personBeneficiariesDto:
        personBeneficiariesDto.length === 0
          ? undefined
          : personBeneficiariesDto,
      organizationBeneficiariesDto:
        organizationBeneficiariesDto.length === 0
          ? undefined
          : organizationBeneficiariesDto,
    };
  }
}
